import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useApi } from '../api';
import { hasItemInTag, type GetTagsResponse, type NestedIds } from './types';

export type TaggableItemType = 'question' | 'submission';

interface UseItemTagsResult {
  /** All tags belonging to the current user (including defaults) */
  availableTags: string[];
  /** Tags that contain the given item */
  itemTags: string[];
  /** Toggle a tag on/off for the current item */
  toggleTag: (tag: string) => void;
  /** Create a new custom tag and immediately add the item to it */
  createAndAddTag: (tag: string) => void;
  /** Whether tags are still loading */
  isLoading: boolean;
}

export function useItemTags(itemType: TaggableItemType, itemId: string): UseItemTagsResult {
  const api = useApi();
  const queryClient = useQueryClient();
  const field = itemType === 'question' ? 'questions' : 'submissions';

  const { data: tagsData, isLoading } = useQuery({
    queryKey: ['user-tags'],
    queryFn: () => api.fetchApi<GetTagsResponse>('/user/tags'),
    staleTime: 60_000,
  });

  const itemTags = useMemo(
    () => tagsData?.tags.filter(t => hasItemInTag(t[field] || {}, itemId)).map(t => t.tag) ?? [],
    [tagsData, itemId, field],
  );

  const allUserTags = tagsData?.tags.map(t => t.tag) ?? [];
  const defaultTags = tagsData?.defaultTags ?? [];
  const availableTags = useMemo(() => [...new Set([...defaultTags, ...allUserTags])], [defaultTags, allUserTags]);

  const optimisticRemove = useCallback((tag: string, id: string) => {
    queryClient.setQueryData<GetTagsResponse>(['user-tags'], (old) => {
      if (!old) return old;
      const tags = old.tags.map(t => {
        if (t.tag !== tag) return t;
        const nested: NestedIds = {};
        for (const [subj, mods] of Object.entries(t[field] || {})) {
          nested[subj] = {};
          for (const [mod, ids] of Object.entries(mods)) {
            nested[subj][mod] = ids.filter(i => i !== id);
          }
        }
        return { ...t, [field]: nested };
      });
      return { ...old, tags };
    });
  }, [queryClient, field]);

  const addMutation = useMutation({
    mutationFn: ({ tag, id }: { tag: string; id: string }) =>
      api.fetchApi<{ tag: string; itemType: string }>(
        `/user/tags/${encodeURIComponent(tag)}/items/${itemType}/${id}`,
        { method: 'PUT' },
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['user-tags'] }),
  });

  const removeMutation = useMutation({
    mutationFn: ({ tag, id }: { tag: string; id: string }) =>
      api.fetchApi<{ tag: string; itemType: string }>(
        `/user/tags/${encodeURIComponent(tag)}/items/${itemType}/${id}`,
        { method: 'DELETE' },
      ),
    onMutate: ({ tag, id }) => optimisticRemove(tag, id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['user-tags'] }),
  });

  const createMutation = useMutation({
    mutationFn: (tag: string) =>
      api.fetchApi<{ tag: string }>('/user/tags', { method: 'POST', body: JSON.stringify({ tag }) }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['user-tags'] }),
  });

  const toggleTag = useCallback((tag: string) => {
    if (itemTags.includes(tag)) {
      removeMutation.mutate({ tag, id: itemId });
    } else {
      addMutation.mutate({ tag, id: itemId });
    }
  }, [itemId, itemTags, addMutation, removeMutation]);

  const createAndAddTag = useCallback((tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    queryClient.setQueryData<GetTagsResponse>(['user-tags'], (old) => {
      if (!old) return old;
      if (old.tags.some(t => t.tag === trimmed)) return old;
      return { ...old, tags: [...old.tags, { tag: trimmed, questions: {}, submissions: {}, isDefault: false }] };
    });
    createMutation.mutate(trimmed, {
      onSuccess: () => {
        addMutation.mutate({ tag: trimmed, id: itemId });
      },
    });
  }, [itemId, queryClient, createMutation, addMutation]);

  return { availableTags, itemTags, toggleTag, createAndAddTag, isLoading };
}
